/**
 * Step3PartitionSetup Component
 * Step 3: Partition 설정 (카드 리스트 + SVG with 색상/호버)
 */

import { generateUuid } from '@/shared/uuid';
import React, { useMemo, useState } from 'react';
import { Box, Typography, Button, Alert } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import type { ReactorComponentId } from '@/features/project-picker/index';
import DiagramPane from './DiagramPane';
import { WIZARD_DIAGRAM_ROW_SX, WIZARD_SIDE_COLUMN_SX } from './wizardLayout';
import PartitionCard from '@/features/project-picker/PartitionCard';
import type { PartitionFormData } from '@/types/project';
import type { WizardFormData } from './types';
import { PARTITION_COLORS } from './types';
import { ALL_SVG_COMPONENTS, coveringScopes, scopeShortLabel, svgComponentsForScopes, uncoveredScopes } from '@/types/scope';
import { modelSlotLabel } from '@/domain/modelSlot';

interface Step3PartitionSetupProps {
  data: WizardFormData;
  onChange: (data: Partial<WizardFormData>) => void;
}

// UUID 생성 헬퍼 — insecure context 폴백은 shared/uuid가 SSOT
const generateId = generateUuid;

// 초기 파티션 생성 (빈 scope로 시작)
const createInitialPartition = (): PartitionFormData => ({
  id: generateId(),
  name: '',
  analysisCode: 'MARS',
  description: '',
  scope: [],
});

const Step3PartitionSetup: React.FC<Step3PartitionSetupProps> = ({ data, onChange }) => {
  const [hoveredPartitionId, setHoveredPartitionId] = useState<string | null>(null);

  // 아직 어떤 계통 모델도 담당하지 않는 스코프 (커버리지 구멍) — 계층 규칙:
  // 그룹 전체를 담당하면 그 계통들도 cover, 계통 전부를 담당하면 그룹도 cover.
  // 제어기·시나리오 참가자의 scope는 "작용 계통"이라 커버리지에 안 센다.
  const unassignedScopes = useMemo(
    () => uncoveredScopes(data.scope, coveringScopes(data.partitions)),
    [data.scope, data.partitions],
  );

  // SVG 하이라이트/흐림 계산 — 기본은 전체 하이라이트, hover 시 해당 파티션에
  // 스포트라이트: 그 파티션의 스코프만 하이라이트하고 다이어그램의 나머지
  // 컴포넌트는 전부 흐리게(dim). "전체 → 하나로 좁히기"만으로는 파티션이
  // 1개일 때 hover 반응이 전혀 안 보였다(#282, QA 관찰).
  const { highlightedComponents, dimmedComponents } = useMemo(() => {
    if (!hoveredPartitionId) {
      // 기본 = 커버리지와 같은 집합(계통 모델의 담당) — 제어기·시나리오의 작용 계통은
      // 검증이 "미할당"으로 볼 수 있으니 여기서도 덮인 것처럼 그리지 않는다
      return {
        highlightedComponents: svgComponentsForScopes(coveringScopes(data.partitions)),
        dimmedComponents: [] as ReactorComponentId[],
      };
    }

    // hover = 그 파티션의 scope 스포트라이트(역할 무관 — 작용 계통도 보여준다).
    // 그림에 없는 계통(CVCS 등)만 담당이면 스포트라이트할 게 없으니 전체를 흐리지 않는다.
    const hoveredPartition = data.partitions.find((p) => p.id === hoveredPartitionId);
    const hovered = svgComponentsForScopes(hoveredPartition?.scope ?? []);
    return {
      highlightedComponents: hovered,
      dimmedComponents: hovered.length ? ALL_SVG_COMPONENTS.filter((c) => !hovered.includes(c)) : [],
    };
  }, [data.partitions, hoveredPartitionId]);

  // 파티션 추가
  const handleAddPartition = () => {
    const newPartition = createInitialPartition();
    onChange({ partitions: [...data.partitions, newPartition] });
  };

  // 파티션 수정
  const handleUpdatePartition = (updated: PartitionFormData) => {
    onChange({
      partitions: data.partitions.map((p) => (p.id === updated.id ? updated : p)),
    });
  };

  // 파티션 삭제
  const handleDeletePartition = (partitionId: string) => {
    onChange({
      partitions: data.partitions.filter((p) => p.id !== partitionId),
    });
  };

  // Partition 색상 가져오기
  const getPartitionColor = (index: number) => {
    return PARTITION_COLORS[index % PARTITION_COLORS.length];
  };

  return (
    <Box sx={WIZARD_DIAGRAM_ROW_SX}>
      {/* 좌측: 계통도 — 2단계와 같은 상자 규칙(DiagramPane) */}
      <DiagramPane highlightedComponents={highlightedComponents} dimmedComponents={dimmedComponents}>
        {/* Partition 범례 — 각주 칸(고정 높이) 안에 가로로 흐른다 */}
        {data.partitions.length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 1.5, rowGap: 0.25 }}>
            <Typography variant="caption" color="text.secondary">
              Partition 범례
            </Typography>
            {data.partitions.map((partition, index) => {
              const color = getPartitionColor(index);
              return (
                <Box
                  key={partition.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    px: 0.75,
                    py: 0.25,
                    borderRadius: 1,
                    bgcolor: hoveredPartitionId === partition.id ? color.bg : 'transparent',
                    transition: 'background-color 0.2s',
                  }}
                  onMouseEnter={() => setHoveredPartitionId(partition.id)}
                  onMouseLeave={() => setHoveredPartitionId(null)}
                >
                  <Box sx={{ width: 12, height: 12, borderRadius: 0.5, bgcolor: color.color, flexShrink: 0 }} />
                  <Typography variant="caption">{partition.name || `${modelSlotLabel(index + 1)}`}</Typography>
                </Box>
              );
            })}
          </Box>
        )}

        <Typography variant="caption" color="text.secondary">
          Partition 카드에 마우스를 올리면 해당 영역이 하이라이트됩니다
        </Typography>
      </DiagramPane>

      {/* 우측: Partition 설정 */}
      <Box sx={{ ...WIZARD_SIDE_COLUMN_SX, py: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            Model Partition
          </Typography>
          <Button
            size="small"
            onClick={handleAddPartition}
            startIcon={<AddIcon />}
            variant="outlined"
          >
            추가
          </Button>
        </Box>

        {/* Partition 카드 목록 */}
        {data.partitions.length === 0 ? (
          <Box
            sx={{
              p: 4,
              border: '2px dashed',
              borderColor: 'divider',
              borderRadius: 2,
              textAlign: 'center',
            }}
          >
            <Typography variant="body2" color="text.secondary" gutterBottom>
              모델을 추가하세요
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              각 모델은 선택한 Scope의 일부를 담당합니다
            </Typography>
            <Button size="small" startIcon={<AddIcon />} onClick={handleAddPartition}>
              모델 추가
            </Button>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            {data.partitions.map((partition, index) => (
              <Box
                key={partition.id}
                onMouseEnter={() => setHoveredPartitionId(partition.id)}
                onMouseLeave={() => setHoveredPartitionId(null)}
                sx={{
                  position: 'relative',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: -8,
                    top: 0,
                    bottom: 0,
                    width: 4,
                    borderRadius: 1,
                    bgcolor: getPartitionColor(index).color,
                  },
                }}
              >
                <PartitionCard
                  partition={partition}
                  index={index}
                  availableScopes={data.scope}
                  onChange={handleUpdatePartition}
                  onDelete={() => handleDeletePartition(partition.id)}
                  canDelete={data.partitions.length > 1}
                />
              </Box>
            ))}
          </Box>
        )}

        {/* 미할당 스코프 경고 */}
        {unassignedScopes.length > 0 && data.partitions.length > 0 && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            미할당 Scope: {unassignedScopes.map(scopeShortLabel).join(', ')}
          </Alert>
        )}

        {/* 파티션 없음 경고 */}
        {data.partitions.length === 0 && (
          <Alert severity="info" sx={{ mt: 2 }}>
            최소 한 개 이상의 모델을 추가해주세요.
          </Alert>
        )}
      </Box>
    </Box>
  );
};

export default Step3PartitionSetup;
